import React from "react";
import { Buildings, Broadcast, IdentificationBadge, Megaphone } from "@phosphor-icons/react";
import StateCheckboxSelector from "@/components/shared/StateCheckboxSelector";
import { EmailTypoHint } from "@/components/user-wizard/EmailTypoHint";

export const KINDS = [
  { key: "buyer", label: "Buyer", hint: "A law firm or company that receives leads", icon: Buildings },
  { key: "affiliate", label: "Affiliate", hint: "A company that sends you traffic", icon: Broadcast },
  { key: "employee", label: "Employee", hint: "A member of your own team", icon: IdentificationBadge },
  { key: "media_buyer", label: "Media buyer", hint: "Runs ads to your landing pages", icon: Megaphone },
];
const INPUT = "w-full border border-black bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black";
const LABEL = "label-mono mb-1 block";

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-zinc-500">{hint}</span>}
    </label>
  );
}

export function TextInput({ testId, ...props }) {
  return <input className={INPUT} data-testid={testId} {...props} />;
}

export function KindStep({ allowed, value, onPick }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="wizard-kind-step">
      {KINDS.map(({ key, label, hint, icon: Icon }) => (
        <button key={key} type="button" disabled={!allowed?.[key]} onClick={() => onPick(key)} data-testid={`wizard-kind-${key}`}
                className={`flex items-start gap-3 border-2 border-black p-4 text-left transition-transform duration-100 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 ${value === key ? "bg-black text-white" : "bg-white"}`}>
          <Icon size={26} weight="bold" />
          <span>
            <span className="block font-chivo text-lg font-black uppercase">{label}</span>
            <span className={`block text-xs ${value === key ? "text-zinc-300" : "text-zinc-600"}`}>{allowed?.[key] ? hint : "You don't have permission for this"}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function ModeToggle({ value, onChange, canNew, noun }) {
  const opts = [["existing", `Existing ${noun}`], ["new", `New ${noun}`]];
  return (
    <div className="mb-4 flex gap-1">
      {opts.map(([k, label]) => (
        <button key={k} type="button" disabled={k === "new" && !canNew} onClick={() => onChange(k)} data-testid={`wizard-company-mode-${k}`}
                className={`border border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 disabled:opacity-40 ${value === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function ExistingPicker({ list, value, onChange, noun }) {
  return (
    <Field label={`Which ${noun}?`} hint={list.length ? null : `No ${noun}s yet. Choose "New ${noun}".`}>
      <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)} data-testid="wizard-company-select">
        <option value="">Pick a {noun}…</option>
        {list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </Field>
  );
}

function ContactEmail({ c, set, label, hint }) {
  return (
    <div>
      <Field label={label} hint={hint}>
        <TextInput testId="wizard-new-contact-email" type="email" value={c.contact_email} onChange={(e) => set({ contact_email: e.target.value })} />
      </Field>
      <EmailTypoHint email={c.contact_email} confirmed={c.contact_email_ok} testId="wizard-contact-email-typo"
        onUse={(fixed) => set({ contact_email: fixed })} onConfirm={() => set({ contact_email_ok: c.contact_email.trim() })} />
    </div>
  );
}

function NewBuyerFields({ c, set }) {
  return (
    <div className="space-y-3">
      <Field label="Company name"><TextInput testId="wizard-new-name" value={c.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <ContactEmail c={c} set={set} label="Email where leads go" hint="Leave empty to use the person's email. You can switch to a webhook later on the buyer's page." />
      <Field label="Price per lead ($)"><TextInput testId="wizard-new-price" type="number" min="1" step="1" value={c.price_per_lead} onChange={(e) => set({ price_per_lead: e.target.value })} /></Field>
      <div><span className={LABEL}>States they buy</span>
        <StateCheckboxSelector value={c.states} onChange={(states) => set({ states })} testId="wizard-new-states" />
      </div>
    </div>
  );
}

function NewAffiliateFields({ c, set }) {
  return (
    <div className="space-y-3">
      <Field label="Company name"><TextInput testId="wizard-new-name" value={c.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <ContactEmail c={c} set={set} label="Company contact email" hint="Leave empty to use the person's email." />
      <Field label="Phone (optional)"><TextInput testId="wizard-new-phone" value={c.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
    </div>
  );
}

export function CompanyStep({ kind, opts, c, set }) {
  const buyer = kind === "buyer";
  const noun = buyer ? "buyer" : "affiliate";
  const canNew = buyer ? opts.allowed.new_buyer : true;
  return (
    <div data-testid="wizard-company-step">
      <ModeToggle value={c.mode} onChange={(mode) => set({ mode })} canNew={canNew} noun={noun} />
      {c.mode === "existing" && <ExistingPicker list={buyer ? opts.buyers : opts.affiliates} value={c.id} onChange={(id) => set({ id })} noun={noun} />}
      {c.mode === "new" && (buyer ? <NewBuyerFields c={c} set={set} /> : <NewAffiliateFields c={c} set={set} />)}
    </div>
  );
}

export function RoleStep({ roles, value, onChange }) {
  return (
    <Field label="Their role" hint="This sets which pages they can see. You can fine-tune permissions later on the Employees page.">
      <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)} data-testid="wizard-role-select">
        <option value="">Pick a role…</option>
        {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </Field>
  );
}

export function PagesStep({ pages, value, onChange }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div data-testid="wizard-pages-step">
      <span className={LABEL}>Landing pages they can work on ({value.length} picked)</span>
      <div className="max-h-64 overflow-y-auto border border-black bg-white">
        {pages.map((p) => (
          <label key={p.id} className="flex cursor-pointer items-center gap-2 border-b border-zinc-200 px-3 py-2 text-sm last:border-0 hover:bg-zinc-50">
            <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`wizard-page-${p.id}`} />
            <span className="flex-1">{p.name}</span>
            {p.shared && <span className="label-mono">Shared funnel</span>}
          </label>
        ))}
        {!pages.length && <div className="p-3 text-xs text-zinc-500">No landing pages yet.</div>}
      </div>
      <p className="mt-1 text-[11px] text-zinc-500">Optional. They only see the pages you tick, and you can change this later.</p>
    </div>
  );
}

export function PersonStep({ form, set }) {
  return (
    <div className="space-y-3" data-testid="wizard-person-step">
      <Field label="Their full name"><TextInput testId="wizard-person-name" value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <Field label="Their email" hint="This is their username. The welcome email goes here.">
        <TextInput testId="wizard-person-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
      </Field>
      <EmailTypoHint email={form.email} confirmed={form.email_ok} testId="wizard-person-email-typo"
        onUse={(fixed) => set({ email: fixed })} onConfirm={() => set({ email_ok: form.email.trim() })} />
    </div>
  );
}
